// src/components/HeroSection.tsx
//
// Home page hero. Answers, in order and within a few seconds: who this is,
// what he builds, that the work is professional, and the two ways to go
// further.
//
// The visual is a system-layer diagram — backend services, APIs,
// integrations, data — rather than a generic terminal or code illustration,
// because the specialism being claimed is backend architecture.
//
// Motion is the same language the About page uses (see ./about/motion), so
// the two pages animate identically.
import { useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import type { Variants } from 'framer-motion';

import { siteConfig } from '../config/siteConfig';
import { homeConfig } from '../config/homeConfig';
import { DURATION, EASE_OUT, STAGGER, motionSet } from './about/motion';
import {
  HeroRoot,
  HeroContainer,
  HeroMain,
  TitleBlock,
  Eyebrow,
  EyebrowMark,
  Name,
  Role,
  Supporting,
  ExperienceNote,
  ExperienceValue,
  ExperienceLabel,
  CapabilityList,
  CapabilityItem,
  CtaRow,
  PrimaryCta,
  LayersPanel,
  LayersLabel,
  LayerList,
  LayerRow,
  MarkerCol,
  LayerMarker,
  LayerConnector,
  LayerBody,
  LayerName,
  LayerDetail,
} from './HeroSection.styles';

const headingId = 'home-hero-name';

/* The connector draws itself downward between markers. With motion reduced it
   simply fades, matching every other reveal on the site. */
const connectorVariants: Variants = {
  hidden: { scaleY: 0 },
  visible: {
    scaleY: 1,
    transition: { duration: DURATION.reveal, ease: EASE_OUT },
  },
};

const connectorVariantsReduced: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: DURATION.revealReduced } },
};
const HeroSection = () => {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion() ?? false;

  const { hero } = homeConfig;
  const motion = motionSet(reduceMotion);
  const rise = motion.reveal;
  const connector = reduceMotion ? connectorVariantsReduced : connectorVariants;

  const goTo = (to: string) => () => navigate(to);

  return (
    <HeroRoot aria-labelledby={headingId}>
      <HeroContainer>
        <HeroMain variants={motion.group(STAGGER.group, 0.04)} initial="hidden" animate="visible">
          <TitleBlock variants={rise}>
            <Eyebrow>
              <EyebrowMark aria-hidden="true" />
              {hero.eyebrow}
            </Eyebrow>
            <Name id={headingId}>{siteConfig.name}</Name>
            <Role>{hero.role}</Role>
          </TitleBlock>
          <Supporting variants={rise}>{hero.supporting}</Supporting>

          <ExperienceNote variants={rise}>
            <ExperienceValue>{hero.experience.value}</ExperienceValue>
            <ExperienceLabel>{hero.experience.label}</ExperienceLabel>
          </ExperienceNote>

          <CapabilityList variants={rise} role="list">
            {hero.capabilityLine.map((item) => (
              <CapabilityItem key={item}>{item}</CapabilityItem>
            ))}
          </CapabilityList>

          <CtaRow variants={rise}>
            <PrimaryCta label={hero.cta.label} onClick={goTo(hero.cta.to)} />
          </CtaRow>
        </HeroMain>

        <LayersPanel
          variants={rise}
          initial="hidden"
          animate="visible"
          aria-label={hero.layersLabel}
        >
          <LayersLabel>{hero.layersLabel}</LayersLabel>
          <LayerList role="list">
            {hero.layers.map((layer, index) => {
              const isLast = index === hero.layers.length - 1;

              return (
                <LayerRow key={layer.name}>
                  <MarkerCol aria-hidden="true">
                    <LayerMarker />
                    {!isLast && (
                      <LayerConnector variants={connector} initial="hidden" animate="visible" />
                    )}
                  </MarkerCol>
                  <LayerBody>
                    <LayerName>{layer.name}</LayerName>
                    <LayerDetail>{layer.detail}</LayerDetail>
                  </LayerBody>
                </LayerRow>
              );
            })}
          </LayerList>
        </LayersPanel>
      </HeroContainer>
    </HeroRoot>
  );
};

export default HeroSection;
